import qs from "qs";

import { UrlProvider } from "../../api/UrlProvider";
import config from "../../config";

const ALLOWED_AUTH_REDIRECT_PROTOCOLS = new Set(["https:"]);

export function getReturnUrl(location = window.location) {
  return `${location.pathname}${location.search}`;
}

/**
 * Login / register / OTP entry routes (pathname only). Used to avoid nesting
 * `redirectTo` when a guest redirect effect re-runs after navigation.
 */
export function isAuthFlowPath(pathWithSearch) {
  if (typeof pathWithSearch !== "string" || !pathWithSearch.startsWith("/")) {
    return false;
  }

  const pathname = pathWithSearch.split("?")[0];
  return /\/(login|login-otp|register)\/?$/i.test(pathname);
}

function encodeRedirectTo(returnUrl) {
  return encodeURIComponent(returnUrl);
}

export const AUTH_TOAST_MESSAGE_MAX_LENGTH = 200;

export function isDisplayableToastMessage(value) {
  return (
    typeof value === "string" &&
    value.trim().length > 0 &&
    value.length <= AUTH_TOAST_MESSAGE_MAX_LENGTH
  );
}

function appendAuthQuery(path, { returnUrl, toastMessage }) {
  const params = {};

  if (returnUrl) {
    params.redirectTo = returnUrl;
  }

  if (isDisplayableToastMessage(toastMessage)) {
    params.toastMessage = toastMessage;
  }

  const query = qs.stringify(params);

  return query ? `${path}?${query}` : path;
}

export function getLoginWithRedirectUrl({
  locale,
  returnUrl = getReturnUrl()
}) {
  return UrlProvider.getUrl("fe.loginWithRedirect", {
    locale,
    redirectUrl: encodeRedirectTo(returnUrl)
  });
}

export function getRegisterWithRedirectUrl({
  locale,
  returnUrl = getReturnUrl(),
  toastMessage
} = {}) {
  return appendAuthQuery(UrlProvider.getUrl("fe.register", { locale }), {
    returnUrl,
    toastMessage
  });
}

export function getLoginOtpWithAuthQueryUrl({
  locale,
  returnUrl,
  toastMessage
}) {
  return appendAuthQuery(UrlProvider.getUrl("fe.loginOtp", { locale }), {
    returnUrl,
    toastMessage
  });
}

const INTERNAL_REDIRECT_BASE = "https://squaber.invalid";

export function isSafeInternalRedirectPath(path) {
  if (
    typeof path !== "string" ||
    !path.startsWith("/") ||
    path.startsWith("//")
  ) {
    return false;
  }

  // Browsers treat "\" as "/" and strip tab, CR, and LF, so "/\evil.com"
  // and a tab between the slashes become protocol-relative URLs.
  if (/[\u0000-\u001F\u007F\\]/.test(path)) {
    return false;
  }

  try {
    const parsed = new URL(path, INTERNAL_REDIRECT_BASE);
    return parsed.origin === INTERNAL_REDIRECT_BASE;
  } catch {
    return false;
  }
}

export function isSafeAuthRedirectUrl(url) {
  if (typeof url !== "string" || !url.trim()) {
    return false;
  }

  let parsed;

  try {
    parsed = new URL(url.trim());
  } catch {
    return false;
  }

  if (!ALLOWED_AUTH_REDIRECT_PROTOCOLS.has(parsed.protocol)) {
    return false;
  }

  // Reject userinfo (e.g. https://evil.com@edukacja.squaber.com/...) — hostname-only
  // allowlisting would otherwise pass; education SSO links never embed credentials.
  if (parsed.username || parsed.password) {
    return false;
  }

  const hostLower = parsed.hostname.toLowerCase();
  const allowedHosts = (config.educationHostNames || []).map(host =>
    String(host).toLowerCase()
  );

  return allowedHosts.includes(hostLower);
}

/**
 * Resolve post-login/register navigation target from `redirectTo`.
 * Accepts a search string or a parsed params object (e.g. from qs).
 * `qs.parse` already URL-decodes values.
 */
export function resolvePostAuthRedirectPath(searchOrParams, fallback = "/") {
  const searchParams =
    typeof searchOrParams === "string"
      ? qs.parse(searchOrParams, { ignoreQueryPrefix: true })
      : searchOrParams || {};

  const { redirectTo } = searchParams;

  if (!isSafeInternalRedirectPath(redirectTo)) {
    return fallback;
  }

  return redirectTo;
}

/**
 * Whether the browser is already showing `targetUrl` (pathname + search),
 * so a hard redirect there would be redundant.
 */
export function isAlreadyAtRedirectTarget(
  targetUrl,
  location = window.location
) {
  return `${location.pathname}${location.search}` === targetUrl;
}
